import React from 'react';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';

const EtTranscriptSection = ({
  title,
  icon: IconComponent,
  data,
  sourceField = '',
  allLabel = 'All',
  contactLabel = 'Contact',
  showDetails,
  setShowDetails,
  renderNoteText,
  renderNoteDate,
  noteTypeField = '',
  typePicField = '',
  magicMomentKey = '',
}) => {

  // Author - Atul Pandey
  // First Created - 27 Apr 2025
  // Purpose to refactor displaying the transcript elements for the Magic Log - i.e. Conversations, Contributions, Impacts, Wins, Challenges, Intentions and Gratiudes
  // Updated - 24 May 2025 - refined JSX - all bg-yellow-300, contact: bg-lime-400

  // 1 - Helper counts
  const allCount = data.filter(item => item[sourceField] === allLabel).length;
  const contactCount = data.filter(item => item[sourceField] === contactLabel).length;
  const totalCount = data.length;

  // Helper: Safely get nested field values
  const getNestedValue = (obj, path) => {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  };
  

  return (
    <>
      {/* Summary Card */}
      <div className="bg-white border border-orange-400 rounded shadow-md p-4 mb-2">
        <div className="grid grid-cols-2 justify-start items-center">
          
          {/* Left: Title and Icon */}
          <div className="col-span-1 flex justify-start items-center">
            <div
              onClick={() => setShowDetails(prev => !prev)}
              className="flex justify-center items-center w-6 h-6 text-orange-600 cursor-pointer rounded-full"
            >
              {showDetails ? <MinusIcon className="h-5 w-5" /> : <PlusIcon className="h-5 w-5" />}
            </div>
            <div className="flex flex-col justify-start items-start ml-2">
              <IconComponent className="h-10 w-10 text-orange-600" />
              <p className="text-sm font-bold text-orange-700">{title}</p>
            </div>
          </div>

          {/* Right: Counts */}
          <div className="col-span-1">
            <div className="grid grid-cols-4 justify-start gap-2 text-[10px] xl:text-xs mb-1">
              <div className="col-span-3">
                <p className="text-gray-800 inline-block rounded-full bg-yellow-300 py-[2px] px-2">{allLabel}</p>
              </div>
              <div className="col-span-1 mx-1">
                <div className="flex justify-center items-center w-6 h-6 rounded-full bg-gray-100 border border-gray-300">
                  <p className="text-xs font-semibold">{allCount}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-4 justify-start gap-2 text-[10px] xl:text-xs mb-1">
              <div className="col-span-3">
                <p className="inline-block rounded-full bg-lime-400 py-[2px] px-2">{contactLabel}</p>
              </div>
              <div className="col-span-1 mx-1">
                <div className="flex justify-center items-center w-6 h-6 rounded-full bg-gray-100 border border-gray-300">
                  <p className="text-xs font-semibold">{contactCount}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-4 justify-start text-[10px] xl:text-xs mb-1 pt-2 mt-2 border-t">
              <div className="col-span-3">
                <p className="font-bold text-sm text-gray-800 inline-block py-[2px] my-1 px-2">Total</p>
              </div>
              <div className="col-span-1 flex justify-center items-center rounded-full bg-gray-100 shadow-md shadow-black">
                <p className="text-sm font-bold text-orange-600">{totalCount}</p>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Detail Cards */}
      {showDetails && data.length > 0 && (
        data
          .slice()
          .sort((a, b) => new Date(renderNoteDate(b)) - new Date(renderNoteDate(a)))
          .map((item, index) => {
            const noteText = renderNoteText(item) || '';
            const noteDate = renderNoteDate(item) || '';
            const typeName = getNestedValue(item, noteTypeField) || "Unknown Type";
            const typePic = getNestedValue(item, typePicField) || "";   
            // const typeName = noteTypeField ? item?.[noteTypeField]?.contributionTypeName || 'Unknown Type' : '';
            // const typePic = typePicField ? item?.[typePicField] || '' : '';
            const isMagicMoment = magicMomentKey && item?.[magicMomentKey];

            return (
              <div key={item._id || index} className="bg-white border border-orange-300 rounded shadow-md p-3 space-y-2">
                
                {/* Header */}
                <div className="flex justify-between items-center border-b pb-2">
                  {/* Left */}
                  <div className="flex justify-start items-center space-x-2">
                    <div className="flex justify-center items-center w-6 h-6 rounded-full bg-orange-100 border border-orange-400">
                      <p className="text-xs font-bold text-orange-700">{index + 1}</p>
                    </div>
                    <div className="flex justify-start items-center mx-1">
                      {typePic && <img src={typePic} alt={typeName} className="h-6 w-6 rounded-full" />}
                      <p className="text-xs font-bold text-orange-700 ml-2">{typeName}</p>
                    </div>
                  </div>

                  {/* Right: SourceType badge */}
                  <div className="flex justify-center items-center bg-purple-600 text-white text-[10px] px-2 py-[2px] rounded-full">
                    {item[sourceField]}
                  </div>
                </div>

                {/* Note Text */}
                {noteText && (
                  <div className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: noteText }} />
                )}

                {/* Footer */}
                <div className="flex justify-between items-center text-[10px] text-gray-400 border-t pt-2 mt-2">
                  <div>{noteDate ? new Date(noteDate).toLocaleDateString() : 'Date Unknown'}</div>
                  {isMagicMoment && (
                    <div className="text-white bg-purple-600 text-[10px] px-2 py-[2px] rounded-full">
                      Magic Moment
                    </div>
                  )}
                </div>

              </div>
            );
          })
      )}

      {data.length === 0 && showDetails && (
        <div className="text-center text-gray-500 italic text-sm">
          No records found during this period.
        </div>
      )}
    </>
  );
};

export default EtTranscriptSection;